/**
 * Correctif Quill — « Cannot read properties of undefined (reading 'emit') »
 *
 * Deux défauts se combinent dans les versions utilisées ici :
 *
 *  1. quill@1.3.7 — le constructeur de `Scroll` démarre son MutationObserver
 *     dans l'appel à `super(domNode)`, et n'affecte `this.emitter` qu'à la
 *     ligne suivante. Toute mutation traitée entre les deux arrive sur une
 *     instance sans émetteur.
 *
 *  2. vue2-editor@2.10.3 — son `beforeDestroy` se contente de
 *     `this.quill = null` : l'observateur reste branché sur un DOM démonté et
 *     continue de déclencher `Scroll.update()` après la destruction. Le même
 *     composant réécrit aussi `quill.root.innerHTML` à chaque changement de
 *     valeur, ce qui détruit les nœuds référencés par les blots.
 *
 * Résultat : des exceptions levées à l'intérieur du callback du
 * MutationObserver, donc hors de tout `try/catch` applicatif et hors du cycle
 * de vie de Vue — impossibles à intercepter depuis les composants.
 *
 * On garde donc `Scroll.update` : si l'émetteur a disparu, l'instance n'est
 * plus vivante, il n'y a personne à prévenir. On coupe l'observateur au
 * passage pour ne pas répéter l'opération à chaque mutation suivante.
 *
 * Le comportement nominal n'est pas modifié : tant que l'émetteur existe,
 * l'implémentation d'origine est appelée telle quelle.
 */
import Quill from "quill";

export default function appliquerCorrectifQuill() {
  const Scroll = Quill.import("blots/scroll");

  if (!Scroll || !Scroll.prototype || Scroll.prototype.__evolisPatch) {
    return;
  }

  const updateOrigine = Scroll.prototype.update;

  Scroll.prototype.update = function (mutations, context) {
    // Émetteur absent = soit l'instance est en cours de construction (Quill
    // l'affecte juste après avoir démarré l'observateur), soit elle est
    // démontée. Dans les deux cas il n'y a personne à prévenir, mais surtout
    // on ne coupe PAS l'observateur : pendant la construction il est encore
    // nécessaire, et le déconnecter laisserait un éditeur inerte, vide.
    if (!this.emitter) {
      return undefined;
    }

    try {
      return updateOrigine.call(this, mutations, context);
    } catch (erreur) {
      // Mutation orpheline (DOM de l'éditeur remplacé ou démonté). On absorbe
      // l'erreur — elle serait sinon levée dans le callback natif du
      // MutationObserver, hors de portée de tout try/catch applicatif — et on
      // coupe l'observateur seulement si l'éditeur a réellement quitté le DOM.
      const detache =
        this.domNode &&
        typeof this.domNode.isConnected === "boolean" &&
        !this.domNode.isConnected;

      if (detache && this.observer && typeof this.observer.disconnect === "function") {
        this.observer.disconnect();
      }
      return undefined;
    }
  };

  Scroll.prototype.__evolisPatch = true;
}
